import {Container, Grid, IconButton, Typography, useMediaQuery, useTheme} from '@material-ui/core';
import {WithStyles, withStyles} from '@material-ui/core/styles';
import get from 'lodash/get';
import isNull from 'lodash/isNull';
import throttle from 'lodash/throttle';
import React, {RefObject, useLayoutEffect, useRef, useState} from 'react';
import {WithTranslation, withTranslation} from 'react-i18next';
import Slider, {Settings} from 'react-slick';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';

import VxLiveWebDesktopScreenshot from 'src/Assets/Images/Cases/VxLiveWebDesktop/user-info.png';
import Slide from 'src/Components/Home/Showcase/Slide';
import {showcaseStyles} from 'src/Components/Home/Showcase/showcaseStyles';
import ArrowLeftIcon from 'src/Components/Icons/Arrows/ArrowLeftIcon';
import ArrowRightIcon from 'src/Components/Icons/Arrows/ArrowRightIcon';

const slides = [VxLiveWebDesktopScreenshot, VxLiveWebDesktopScreenshot, VxLiveWebDesktopScreenshot];

const getPadding = (width: number) => {
	if (width < 1232) {
		return 24;
	}

	return (width - 1232) / 2;
};

const Showcase = (props: IShowcaseProps) => {
	const theme = useTheme();
	const matchesMobile = useMediaQuery(theme.breakpoints.down('xs'));
	const sliderRef: RefObject<Slider> = useRef(null);
	const wrapperRef: RefObject<HTMLDivElement> = useRef(null);
	const [windowWidth, setWindowWidth] = useState(window.innerWidth);
	const [width, setWidth] = useState(0);
	const [containerPadding, setContainerPadding] = useState(getPadding(windowWidth));
	const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
	const resizeHandler = throttle(() => {
		setWidth((get(wrapperRef, 'current.clientWidth', 0) / 100) * 85);
		setWindowWidth(window.innerWidth);
	}, 100);

	useLayoutEffect(() => {
		resizeHandler();
		window.addEventListener('resize', resizeHandler);
		return () => {
			window.removeEventListener('resize', resizeHandler);
		};
	});

	useLayoutEffect(() => {
		setContainerPadding(getPadding(window.innerWidth));
	}, [windowWidth]);

	const settings: Settings = {
		dots: matchesMobile,
		speed: 500,
		slidesToShow: 1,
		slidesToScroll: 1,
		arrows: false,
		variableWidth: true,
		appendDots: (dots: React.ReactNode): JSX.Element => {
			return <ul className={props.classes.dots}>{dots}</ul>;
		},
		afterChange: (currentSlide: number) => {
			setCurrentSlideIndex(currentSlide);
		},
	};

	const onPrevClick = () => {
		if (!isNull(sliderRef.current)) {
			sliderRef.current.slickPrev();
		}
	};

	const onNextClick = () => {
		if (!isNull(sliderRef.current)) {
			sliderRef.current.slickNext();
		}
	};

	return (
		<section className={props.classes.root}>
			<Container className={props.classes.container} maxWidth={false}>
				<Grid container spacing={0} style={{paddingLeft: containerPadding}}>
					<Grid item className={props.classes.textBlock}>
						<Typography variant="body1" className={props.classes.label}>
							{props.t('slogan.label')}
						</Typography>
						<Typography variant="h1" className={props.classes.catchPhrase}>
							{props
								.t('slogan.main')
								.split(' ')
								.map((word, key) => (
									<span key={key} className="line">
										{word}
									</span>
								))}
						</Typography>
						{!matchesMobile && (
							<div className={props.classes.arrows}>
								<IconButton onClick={onPrevClick} className={props.classes.arrowButton} disableRipple>
									<ArrowLeftIcon />
								</IconButton>
								<IconButton onClick={onNextClick} className={props.classes.arrowButton} disableRipple>
									<ArrowRightIcon />
								</IconButton>
							</div>
						)}
					</Grid>
					<Grid ref={wrapperRef} item className={props.classes.slideshow}>
						<Slider {...settings} ref={sliderRef}>
							{slides.map((slide: string, index: number) => (
								<Slide
									width={width}
									slide={slide}
									index={index}
									key={index}
									currentSlideIndex={currentSlideIndex}
									description={slide}
								/>
							))}
						</Slider>
					</Grid>
				</Grid>
			</Container>
		</section>
	);
};

interface IShowcaseProps extends WithStyles, WithTranslation {}

export default withStyles(showcaseStyles)(withTranslation('showcase')(Showcase));
